/**
 * Pick whole units from the industry-standard catalogue and drop them into a custom template.
 */
import React, { useEffect, useMemo, useState } from "react";
import { translateUi } from "../i18n/index";
import { CheckCircle2, ClipboardList, X } from "../icons";
import { cb, cbCn } from "./cycleBuilderTheme";
import type { WizardCriteriaItem } from "./CycleWizardStore";
import {
  flattenLibraryUnitsToWizardItems,
  industryUnitDisplayTitle,
  wizardItemLineKey,
  type LibraryUnit,
} from "./cycleLibraryUi";

function unitStats(unit: LibraryUnit): { elements: number; criteria: number; lineKeys: string[] } {
  const lineKeys = flattenLibraryUnitsToWizardItems([unit]).map(wizardItemLineKey);
  return { elements: unit.elements?.length ?? 0, criteria: lineKeys.length, lineKeys };
}

export function IndustryUnitPickerDialog({
  open,
  units,
  items,
  onClose,
  onAdd,
  isDarkMode = false,
  isMobile = false,
}: {
  open: boolean;
  units: LibraryUnit[];
  items: WizardCriteriaItem[];
  onClose: () => void;
  onAdd: (units: LibraryUnit[]) => void;
  isDarkMode?: boolean;
  isMobile?: boolean;
}) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (open) setSelected({});
  }, [open]);

  const existingKeys = useMemo(() => new Set(items.map(wizardItemLineKey)), [items]);

  const rows = useMemo(
    () =>
      units.map((unit) => {
        const stats = unitStats(unit);
        const already = stats.lineKeys.filter((k) => existingKeys.has(k)).length;
        return {
          unit,
          title: industryUnitDisplayTitle(unit),
          elements: stats.elements,
          criteria: stats.criteria,
          already,
          remaining: stats.criteria - already,
        };
      }),
    [units, existingKeys]
  );

  if (!open) return null;

  const selectedUnits = rows.filter((r) => selected[r.unit.id]).map((r) => r.unit);
  const addableRows = rows.filter((r) => r.remaining > 0);
  const allAddableSelected = addableRows.length > 0 && addableRows.every((r) => selected[r.unit.id]);
  const newCriteria = rows.filter((r) => selected[r.unit.id]).reduce((n, r) => n + r.remaining, 0);

  const toggle = (id: string) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const toggleAll = () => {
    if (allAddableSelected) {
      setSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of addableRows) next[r.unit.id] = true;
    setSelected(next);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-6">
      <div
        className={cbCn(`my-auto w-full max-w-3xl ${isMobile ? "p-4" : "p-6"}`, cb.modal(isDarkMode))}
        role="dialog"
        aria-modal="true"
        aria-labelledby="industry-unit-picker-title"
      >
        <div className="flex items-start gap-3">
          <span
            className={cbCn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-100 text-indigo-700"
            )}
            aria-hidden
          >
            <ClipboardList className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h4 id="industry-unit-picker-title" className={cbCn("text-lg font-semibold", cb.heading(isDarkMode, true))}>
              {translateUi("Add industry standard units")}
            </h4>
            <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
              {translateUi("Tick the units you need. Every element and criterion comes with them — you can edit or delete lines afterwards.")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cbCn(cb.iconBtn(isDarkMode, isMobile))}
            aria-label={translateUi("Close")}
            title={translateUi("Close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <span className={cbCn("text-xs font-medium tabular-nums", cb.bodySm(isDarkMode))}>
            {translateUi("{{n}} selected", { n: selectedUnits.length })}
          </span>
          <button
            type="button"
            onClick={toggleAll}
            disabled={addableRows.length === 0}
            className={cbCn(
              "text-xs font-medium disabled:opacity-40",
              isDarkMode ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800"
            )}
          >
            {allAddableSelected ? translateUi("Deselect all") : translateUi("Select all")}
          </button>
        </div>

        {addableRows.length === 0 ? (
          <div
            className={cbCn(
              "mt-2 rounded-lg border px-3 py-2 text-sm",
              isDarkMode ? "border-emerald-500/30 bg-emerald-950/40 text-emerald-200" : "border-emerald-200 bg-emerald-50 text-emerald-800"
            )}
          >
            {translateUi("Every industry standard unit is already on this template.")}
          </div>
        ) : null}

        <div className={cbCn("mt-2 grid max-h-[min(52vh,30rem)] gap-2 overflow-y-auto pr-1", isMobile ? "grid-cols-1" : "sm:grid-cols-2")}>
          {rows.map((row) => {
            const isSelected = !!selected[row.unit.id];
            const fullyAdded = row.remaining === 0;
            return (
              <button
                key={row.unit.id}
                type="button"
                aria-pressed={isSelected}
                disabled={fullyAdded}
                title={fullyAdded ? translateUi("Every criterion of this unit is already on the template.") : undefined}
                onClick={() => toggle(row.unit.id)}
                className={cbCn(
                  "flex items-start gap-3 rounded-xl border p-3 text-left transition-colors",
                  isMobile && "min-h-[64px]",
                  fullyAdded
                    ? isDarkMode
                      ? "cursor-not-allowed border-slate-800 bg-slate-900/40 opacity-60"
                      : "cursor-not-allowed border-slate-200 bg-slate-100/70 opacity-70"
                    : isSelected
                      ? isDarkMode
                        ? "border-indigo-400 bg-indigo-950/45"
                        : "border-indigo-500 bg-indigo-50/90"
                      : isDarkMode
                        ? "border-slate-700 bg-slate-900/80 hover:border-indigo-500/40"
                        : "border-slate-200 bg-white hover:border-indigo-300"
                )}
              >
                <span
                  className={cbCn(
                    "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded",
                    fullyAdded
                      ? isDarkMode
                        ? "bg-emerald-900/50 text-emerald-300"
                        : "bg-emerald-100 text-emerald-700"
                      : isSelected
                        ? "bg-indigo-600 text-white"
                        : isDarkMode
                          ? "border border-slate-600 bg-slate-800"
                          : "border border-slate-300 bg-white"
                  )}
                  aria-hidden
                >
                  {isSelected || fullyAdded ? <CheckCircle2 className="h-4 w-4" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cbCn(
                      "block text-sm font-semibold leading-snug break-words",
                      fullyAdded
                        ? isDarkMode
                          ? "text-slate-400"
                          : "text-slate-500"
                        : isDarkMode
                          ? "text-slate-50"
                          : "text-slate-900"
                    )}
                  >
                    {row.title}
                  </span>
                  <span className={cbCn("mt-1 block text-xs", cb.bodySm(isDarkMode))}>
                    {row.elements} {translateUi("elements")} · {row.criteria} {translateUi("criteria")}
                  </span>
                  {row.already > 0 ? (
                    <span
                      className={cbCn(
                        "mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium",
                        fullyAdded
                          ? isDarkMode
                            ? "bg-emerald-950/60 text-emerald-200 ring-1 ring-emerald-500/30"
                            : "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200"
                          : isDarkMode
                            ? "bg-amber-950/60 text-amber-200"
                            : "bg-amber-100 text-amber-800"
                      )}
                    >
                      {fullyAdded
                        ? translateUi("Already on this template")
                        : translateUi("{{n}} already added", { n: row.already })}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <span className={cbCn("mr-auto text-xs", cb.bodySm(isDarkMode))}>
            {translateUi("{{n}} criteria will be added", { n: newCriteria })}
          </span>
          <button type="button" onClick={onClose} className={cbCn("rounded border px-3 py-2 text-sm", cb.btnSecondary(isDarkMode))}>
            {translateUi("Cancel")}
          </button>
          <button
            type="button"
            disabled={selectedUnits.length === 0}
            onClick={() => {
              onAdd(selectedUnits);
              onClose();
            }}
            className="rounded bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {translateUi("Add units")}
          </button>
        </div>
      </div>
    </div>
  );
}
